import { useState, useEffect, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { Box, createTheme, ThemeProvider, AppBar, Toolbar, IconButton, Typography, TextField, Box as MuiBox, Paper, List, ListItem, ListItemText, ListItemIcon, Popper, ClickAwayListener } from "@mui/material";
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useNavigate } from 'react-router-dom';
import { ToastProvider } from "./contexts/ToastContext";
import { AuthProvider } from "./contexts/AuthContext";
import Sidebar from "./Components/SideBar";
import PageRoutes from "./Routes/PageRoutes";
import Login from "./Pages/Login";
import ResetPage from "./Pages/Resetpage";
import ProtectedRoute from './Components/ProtectedRoute';
import { getSidebarItemsForSearch } from "./Utils/sidebarData";

const theme = createTheme({
  palette: {
    success: {
      main: "#4BCE97",
      background: "#EDFAF5",
    },
    primary: {
      main: "#FF6600",
      background: "#F7F8FA",
      border: "#C3C3C3"
    },
    secondary: {
      main: "#FF6600",
      sidemenutext: "#000000",
      background: "#FFFFFF",
      textColor: "#C3C3C3"
    },
    error: {
      main: "#FF0000",
    },
    default: {
      main: "#445371",
      background: "#44537126",
    },
  },
  typography: {
    fontFamily: ["Inter", "sans-serif"].join(","),
    fontSize: 14,
    fontWeightBold: 500,
    h1: {
      fontFamily: "Inter, sans-serif",
      fontWeight: 300,
      fontSize: "80px",
    },
    h2: {
      fontFamily: "Inter, sans-serif",
      fontWeight: 300,
      fontSize: "48px",
    },
    h3: {
      fontFamily: "Inter, sans-serif",
      fontWeight: 500,
      fontSize: "22px",
    },
    h4: {
      fontFamily: "Inter, sans-serif",
      fontWeight: 400,
      fontSize: "20px",
      lineHeight: "23.12px",
      letterSpacing: "0.00735em",
    },
    h5: {
      fontFamily: "Inter, sans-serif",
      fontWeight: 500,
      fontSize: "18px",
      lineHeight: "26.67px",
    },
    h6: {
      fontFamily: "Inter, sans-serif",
      fontWeight: 500,
      fontSize: "16px",
      lineHeight: "25.6px",
    },
    tableheading: {
      fontFamily: "Inter, sans-serif",
      fontWeight: 500,
      fontSize: "14px",
      lineHeight: "25.6px",
    },
    body1: {
      fontFamily: "Inter, sans-serif",
      fontWeight: 400,
      fontSize: "14px",
      lineHeight: 1.5,
      letterSpacing: "0.00938em",
    },
    body2: {
      fontFamily: "Inter, sans-serif",
      fontWeight: 400,
      fontSize: "12px",
      lineHeight: "20px",
      letterSpacing: "0.01071em",
      color: "#000000",
    },
    button: {
      fontFamily: "Inter, sans-serif",
      fontWeight: 400,
      fontSize: "14px",
      lineHeight: "24.5px",
      letterSpacing: "0.02857em",
      textTransform: "none",
    },
    caption: {
      fontFamily: "Inter, sans-serif",
      fontWeight: 400,
      fontSize: "12px",
      lineHeight: "19.92px",
      letterSpacing: "0.03333em",
    },
    overline: {
      fontFamily: "Inter, sans-serif",
      fontWeight: 400,
      fontSize: "12px",
      lineHeight: "21.33px",
      letterSpacing: "0.08333em",
      textTransform: "uppercase",
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          boxShadow: "none",
          textTransform: "none",
        },
        contained: {
          boxShadow: "none",
          "&:hover": {
            boxShadow: "none",
          },
        },
        outlined: {
          boxShadow: "none",
          "&:hover": {
            boxShadow: "none",
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
          "& .MuiOutlinedInput-root": {
            borderColor: "primary.border",
            "& fieldset": {
              borderColor: "primary.border",
            },
            "&:hover fieldset": {
              borderColor: "primary.border",
            },
            "&.Mui-focused fieldset": {
              borderColor: "primary.border",
            },
          },
          "& .MuiInputBase-input": {
            fontSize: "14px",
            fontFamily: "sans-serif",
            color: "primary.main",
          },
        },
      },
    },
  },
});

// This component ensures the Router context is available for all child components
const AppContent = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  // Suggestions are derived from the query, so no state needed
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchAnchorEl, setSearchAnchorEl] = useState(null);
  const themeMUI = useTheme();
  const isSmallScreen = useMediaQuery(themeMUI.breakpoints.down('md'));
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const navigate = useNavigate();

  const drawerWidth = 240;

  // Get sidebar data for search suggestions
  const sidebarItems = useMemo(() => getSidebarItemsForSearch(), []);

  const searchSuggestions = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    if (!normalizedQuery) {
      return [];
    }

    return sidebarItems
      .filter(item =>
        item.title.toLowerCase().includes(normalizedQuery)
      )
      .slice(0, 5);
  }, [searchQuery, sidebarItems]);

  const handleDrawerToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  // Reset highlighted item when suggestions change
  useEffect(() => {
    if (searchSuggestions.length === 0) {
      setHighlightedIndex(-1);
      return;
    }

    setHighlightedIndex((prevIndex) => {
      if (prevIndex >= searchSuggestions.length) {
        return searchSuggestions.length - 1;
      }
      return prevIndex;
    });
  }, [searchSuggestions]);

  const handleSearchFocus = (event) => {
    setSearchAnchorEl(event.currentTarget);
    setShowSuggestions(true);
  };

  const handleSearchBlur = () => {
    // Delay blur to allow click events to fire first
    setTimeout(() => {
      setShowSuggestions(false);
    }, 200);
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchQuery('');
    setHighlightedIndex(-1);
    setShowSuggestions(false);
    navigate(suggestion.path); // Navigate to the selected page
  };

  const handleSuggestionMouseDown = (e, suggestion) => {
    // Prevent the input from losing focus
    e.preventDefault();
    handleSuggestionClick(suggestion);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!showSuggestions) {
        setShowSuggestions(true);
      }
      if (searchSuggestions.length === 0) {
        return;
      }
      setHighlightedIndex((prevIndex) => {
        const nextIndex = prevIndex < searchSuggestions.length - 1 ? prevIndex + 1 : 0;
        return nextIndex;
      });
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (!showSuggestions || searchSuggestions.length === 0) {
        return;
      }
      setHighlightedIndex((prevIndex) => {
        if (prevIndex <= 0) {
          return searchSuggestions.length - 1;
        }
        return prevIndex - 1;
      });
    } else if (event.key === 'Enter') {
      if (showSuggestions && searchSuggestions.length > 0) {
        event.preventDefault();
        const suggestion =
          highlightedIndex >= 0
            ? searchSuggestions[highlightedIndex]
            : searchSuggestions[0];
        handleSuggestionClick(suggestion);
      }
    }
  };

  return (
    <Box sx={{ display: 'flex' }}>
      {/* Sidebar */}
      <Sidebar
        open={isSmallScreen ? sidebarOpen : sidebarOpen}
        onClose={handleDrawerToggle}
      />

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 0,
          width: isSmallScreen ? '100%' : (sidebarOpen ? `calc(100% - ${drawerWidth}px)` : `calc(100% - 72px)`),
          transition: (theme) =>
            theme.transitions.create(['width'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
          maxWidth: 'none',
          overflowX: 'hidden',
          mt: isSmallScreen ? 0 : 8, // Add top margin for desktop navbar
        }}
      >
        {/* Desktop Navbar */}
        {!isSmallScreen && (
          <AppBar
            position="fixed"
            sx={{
              backgroundColor: '#111111',
              boxShadow: 'none',
              borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
              zIndex: (theme) => theme.zIndex.drawer - 1,
              left: sidebarOpen ? drawerWidth : 88,
              width: sidebarOpen ? `calc(100% - ${drawerWidth}px)` : `calc(100% - 88px)`,
              transition: (theme) =>
                theme.transitions.create(['left', 'width'], {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.enteringScreen,
                }),
            }}
          >
            <Toolbar sx={{ justifyContent: 'space-between' }}>
              <MuiBox sx={{ display: 'flex', alignItems: 'center', gap: 2, position: 'relative' }}>
                <TextField
                  size="small"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setShowSuggestions(true);
                    setHighlightedIndex(-1);
                  }}
                  onFocus={handleSearchFocus}
                  onBlur={handleSearchBlur}
                  onKeyDown={handleKeyDown}
                  sx={{
                    width: 320,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      borderRadius: 2,
                      '& fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.5)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.8)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#FF6600',
                      },
                    },
                    '& input::placeholder': {
                      color: '#fff',
                    },
                    '& input': {
                      color: '#FFFFFF',
                    },
                  }}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ color: '#FF6600', mr: 1 }} />,
                  }}
                />

                {/* Suggestions Dropdown */}
                {showSuggestions && searchSuggestions.length > 0 && (
                  <Paper
                    onMouseDown={(e) => e.preventDefault()}
                    sx={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      width: 300,
                      backgroundColor: '#1A1C1E',
                      borderRadius: 2,
                      mt: 1,
                      boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                      border: '1px solid rgba(255, 255, 255, 0.05)',
                      zIndex: 9999,
                      overflow: 'hidden'
                    }}
                  >
                    <List sx={{ p: 0.5 }}>
                      {searchSuggestions.map((suggestion, index) => (
                        <ListItem
                          key={suggestion.id || index}
                          onMouseDown={(e) => handleSuggestionMouseDown(e, suggestion)}
                          onMouseEnter={() => setHighlightedIndex(index)}
                          sx={{
                            cursor: 'pointer',
                            borderRadius: 1.5,
                            mb: 0.5,
                            py: 1,
                            px: 1.5,
                            backgroundColor:
                              highlightedIndex === index
                                ? 'rgba(255, 102, 0, 0.1)'
                                : 'transparent',
                            boxShadow:
                              highlightedIndex === index
                                ? 'inset 0 0 0 1px rgba(255, 102, 0, 0.2)'
                                : 'none',
                            '&:last-child': {
                              mb: 0,
                            },
                          }}
                        >
                          <ListItemIcon
                            sx={{
                              minWidth: 40,
                              color: '#FF6600',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {suggestion.icon}
                          </ListItemIcon>
                          <ListItemText
                            primary={suggestion.title}
                            primaryTypographyProps={{
                              sx: {
                                color: '#FFFFFF',
                                fontSize: '14px',
                                fontWeight: 500,
                                ml: 1
                              }
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                )}
              </MuiBox>
              <Typography variant="h6" component="div" sx={{ color: '#FFFFFF', fontWeight: 600 }}>
                Dashboard
              </Typography>
            </Toolbar>
          </AppBar>
        )}

        {/* Mobile AppBar */}
        {isSmallScreen && (
          <AppBar
            position="fixed"
            sx={{
              zIndex: (theme) => theme.zIndex.drawer - 1,
              backgroundColor: '#111111',
              boxShadow: 'none',
              borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
            }}
          >
            <Toolbar>
              <IconButton edge="start" color="inherit" sx={{ color: '#FFFFFF' }} aria-label="menu" onClick={handleDrawerToggle}>
                <MenuIcon />
              </IconButton>
              <Typography variant="h6" component="div" sx={{ ml: 1, color: '#FFFFFF', fontWeight: 600 }}>
                Dashboard
              </Typography>
            </Toolbar>
          </AppBar>
        )}

        <Box sx={{ p: 1, pt: isSmallScreen ? 10 : 3, mt: isSmallScreen ? 10 : 0 }}>
          <PageRoutes />
        </Box>
      </Box>
    </Box>
  );
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <Router>
        <AuthProvider>
          <ToastProvider>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/reset/:token" element={<ResetPage />} />
              <Route
                path="/*"
                element={
                  <ProtectedRoute>
                    <AppContent />
                  </ProtectedRoute>
                }
              />
            </Routes>
          </ToastProvider>
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
}

export default App;
